import {
  handleize,
  focusFirstInputOfForm,
  addCloseListener,
  clearAllCloseListeners,
  convertGQLToProductJson,
  decodeObjectID,
  getProductLimit,
} from 'utils/general';
import bus from 'lib/bus';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import yotpo from 'lib/yotpo';
import * as gqlQueries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import nanoajax from 'nanoajax';

/**
 * @info
 *
 * *The "store/global" directory*
 * This "store" directory declares each of the Vuex
 * modules that all together power the site. Here, we have the
 * Vuex store module that sits behind cart functionality.
 *
 * *This file*
 * This file outlines "actions". Actions are functions that
 * include logic that, when ready, commits a mutation to the
 * application state. Whereas mutations must be synchronous,
 * actions return a promise and can include asynchronous
 * logical sequences.
 */

// --- Modals ---
let openedModals = [];
const closeModalActions = ({ commit, state }) => {
  // Set Focus on Modal Trigger
  const modalHandle = openedModals[openedModals.length - 1]
  if (modalHandle) {
    const modalTrigger = document.querySelector(`[data-modal-handle="${modalHandle}"]`)
    if (modalTrigger) {
      setTimeout(() => {
        modalTrigger.focus();
      }, 100);
    }
  }

  openedModals.pop();
  if (openedModals.length === 0) {
    // Reset variables
    openedModals = [];
    // Clear listeners
    clearAllCloseListeners();
    clearAllBodyScrollLocks();
    commit('openModal', '');
  } else {
    // Saving and removing the last item because it will be added again
    const key = openedModals[openedModals.length - 1];
    openedModals.pop();
    // Reopening modal
    openModalAction({ commit, state }, key);
  }
};
const openModalAction = ({ commit, state }, payload) => {
  openedModals.push(payload);
  addCloseListener(() => {
    closeModalActions({ commit, state });
  });
  commit('openModal', payload);
};

// --- Account ---
export default {
  openLoginFormModal({ commit, state }, payload) {
    commit('setForm', 'login');
    openedModals = [];
    openModalAction({ commit, state }, 'accountForms');
    focusFirstInputOfForm(payload.form, 100);
  },
  setForm({ commit, state }, payload) {
    commit('setForm', payload.type);
    focusFirstInputOfForm(payload.form, 100);
  },
  appendModal({ commit, state }, payload) {
    openModalAction({ commit, state }, payload);
  },
  openModal({ commit, state }, payload) {
    openedModals = [];
    openModalAction({ commit, state }, payload);
  },
  closeModal({ commit, state }) {
    closeModalActions({ commit, state });
  },
  checkresolution({ commit, state }) {
    commit('refreshResolution');
  },
  updateCustomerFirstName({ commit }, payload) {
    commit('setCustomerFirstName', payload);
  },
  updateCustomerLastName({ commit }, payload) {
    commit('setCustomerLastName', payload);
  },

  updateCustomerEmail({ commit }, payload) {
    commit('setCustomerEmail', payload);
  },
  updateCustomerPhone({ commit }, payload) {
    commit('setCustomerPhone', payload);
  },
  updateCustomerBirthDay({ commit }, payload) {
    commit('setCustomerBirthDay', payload);
  },
  updateCustomerBirthMonth({ commit }, payload) {
    commit('setCustomerBirthMonth', payload);
  },
  updateAllCustomerFields({ commit }, payload) {
    Object.keys(payload).forEach((customerField) => {
      if (customerField === 'firstName') {
        commit('setCustomerFirstName', payload[customerField]);
      }

      if (customerField === 'lastName') {
        commit('setCustomerLastName', payload[customerField]);
      }

      if (customerField === 'email') {
        commit('setCustomerEmail', payload[customerField]);
      }

      if (customerField === 'phone') {
        const phoneValue = payload[customerField] || '---';

        commit('setCustomerPhone', phoneValue);
      }

      if (customerField === 'birthDay') {
        commit('setCustomerBirthDay', payload[customerField]);
      }

      if (customerField === 'birthMonth') {
        commit('setCustomerBirthMonth', payload[customerField]);
      }
    });
  },

  // --- GWP ---
  async requestGWPCollection({ commit }, handle) {
    const products = [];

    const getProducts = (cursor = null) =>
      storefront
        .request(gqlQueries.collectionForGwp, { handle, first: 200, cursor })
        .then((data) => {
          const responseProducts = data.collection.products;
          const response = responseProducts.edges
            .map((p) => ({
              id: decodeObjectID(p.node.id),
              variants: p.node.variants.edges
                .map((variant) => {
                  if (!!variant.node.sourceGWPCollectionItem) {
                    return {
                      id: decodeObjectID(variant.node.id, 'ProductVariant'),
                      sourceGWPCollectionItem:
                        !!variant.node.sourceGWPCollectionItem,
                    };
                  }
                  return false;
                })
                .filter((v) => v),
            }))
            .filter((el) =>
              el.variants.some(
                (variant) => variant && variant.sourceGWPCollectionItem,
              ),
            );

          products.push(...response);

          if (responseProducts.pageInfo.hasNextPage) {
            const lastProductIndex = responseProducts.edges.length - 1;
            cursor = responseProducts.edges[lastProductIndex].cursor;
          }

          return responseProducts;
        });

    // Make request for GWP collection data
    const resp = await getProducts();

    // If there are more products, make an additional request for the next page
    // We could convert this to a function that continues to request until
    // we've collected all pages, but we're continuing to limit this to two
    // requests
    if (resp.pageInfo.hasNextPage) {
      const cursor = resp.edges[resp.edges.length - 1].cursor;
      await getProducts(cursor);
    }

    commit('setGWPCollection', products);
  },
  setGWPCollectionLimit({ commit }, limit) {
    commit('setGWPCollectionLimit', limit);
  },
  setGWPThresholds({ commit }, thresholds) {
    commit('setGWPThresholds', thresholds);
  },
  setRewardCollectionLimit({ commit }, limit) {
    commit('setRewardCollectionLimit', limit);
  },
  clearUserSelection({ commit }) {
    commit('clearUserSelection');
  },
  setGWPLoading({ commit }, data) {
    commit('setGWPLoading', data);
  },

  addRemoveProductToList(
    { commit, state, getters, dispatch },
    { id, tags, prev = false, productType = 'alt', otherData = false },
  ) {
    const currentID = parseInt(id);
    const prevID = prev ? parseInt(prev.id) : false;
    const itemProperties = {
      _gwp: true,
      _item_list_id: 'GWP',
      _item_list_name: 'GWP',
      _product_tags: tags,
      _index: 1,
      _product_rating: otherData?.reviews_average || '',
    };

    if (!prevID) {
      // Remove previously selected product
      const gifts = state.cart.items.find((el) => el.properties._gwp && el.variant_id === currentID);
      if (gifts) {
        this.dispatch('cart/updateCartLine', { id: gifts.key, quantity: 0 });
        commit('removeAltProductToList', { id: currentID });
      } else {
        const sameProductsInCart = state.cart.items.filter(
          (cart_item) => Number(cart_item.id) === Number(currentID),
        );

        const sameIDProductsQuantity = sameProductsInCart?.reduce(
          (acc, rec) => (acc = acc + rec.quantity),
          0,
        );

        this.dispatch('cart/addToCart', {
          id: currentID,
          quantity: 1,
          properties: itemProperties,
        });

        commit('addRemoveAltProductToList', { id: currentID });

        if (sameIDProductsQuantity > 4) {
          this.dispatch('cart/updateCartLine', {
            id: sameProductsInCart[0].key,
            quantity: sameProductsInCart[0].quantity - 1,
          });
        }
      }
    } else {
      // On shade change
      const IDs = getters.gwpAlreadyAltAddedToCart.map((p) => parseInt(p.id));
      let targetID = prevID;
      if (!targetID) {
        targetID = id;
      }
      if (IDs.includes(targetID)) {
        const gifts = state.cart.items.find((el) => el.properties._gwp && el.variant_id === targetID);
        this.dispatch('cart/updateCartLine', { id: gifts.key, quantity: 0 });
        commit('addRemoveAltProductToList', { id: targetID });

        setTimeout(() => {
          this.dispatch('cart/addToCart', {
            id: currentID,
            quantity: 1,
            properties: itemProperties,
          });
        }, 500);

        if (!prevID) {
          commit('addRemoveAltProductToList', { id: targetID });
        }
      } else {
        const data = prev || {};
        if (prev) {
          data.original = currentID;
          data.properties = {
            _gwp: true,
            _gwp_original_shadow: currentID,
            _gwp_prev_shadow: prevID,
          };
        } else {
          data.id = id;
          data.properties = {
            _gwp: true,
          };
        }

        if (productType === 'alt') {
          this.dispatch('cart/addToCart', {
            id: currentID,
            quantity: 1,
            properties: data.properties,
          });
          commit('addRemoveAltProductToList', data);
        } else {
          this.dispatch('cart/addToCart', {
            id: currentID,
            quantity: 1,
            properties: data.properties,
          });
          commit('addRemoveProductToList', currentID);
        }
      }
    }
  },
  requestRewardsCollection({ commit, state }, handle) {
    commit('setGWPLoading', true);
    storefront
      .request(gqlQueries.collection, { handle, first: 30 })
      .then((data) => {
        let response = [];
        if (data.collection) {
          response = data.collection.products.edges.map((p) =>
            convertGQLToProductJson(p.node),
          );
        }
        const promises = [];
        if (response.length > 0) {
          response.map((e) => {
            const product = e;

            const gwpVariants = product.variants.some(
              (variant) => !!variant.isGWPVariant,
            );

            if (gwpVariants && product.available) {
              const handle = handleize(
                e.title.split(window.__GLOBAL__.productDelimiter)[0],
              );
              const url = `${window.Shopify.routes.root}collections/${handle}?view=collection-shade`;
              promises.push(
                new Promise((resolve, reject) => {
                  nanoajax.ajax({ url }, (code, res) => {
                    if (res.length <= 4) {
                      product.shadeCount = parseInt(res, 10);
                    }
                    resolve(res);
                  });
                }),
              );
            }
            return product;
          });
        }
        return Promise.all(promises).then((responses) => {
          const filteredGWP = response
            .map((product) => {
              const gwpVariants = product.variants.filter(
                (variant) => !!variant.isGWPVariant,
              );
              return { ...product, variants: gwpVariants };
            })
            .filter((p) => p.variants.length);

          commit('setRewardCollection', filteredGWP);
          commit('setGWPLoading', false);
        });
      });
  },

  removeAltProductToList({ commit, state }, id) {
    commit('removeAltProductToList', { id });
  },

  async removeAllGWP({ commit, state }) {
    const productsToRemove = state.cart.items.filter((p) => p.properties._gwp);
    const promises = [];

    for (const [index, item] of productsToRemove.entries()) {
      const singleData = {
        quantity: 0,
        id: item.key,
        callback: () => {
          commit('removeAltProductToList', { id: item.id });
        },
      };
      const promise = new Promise((resolve, reject) => {
        this.dispatch('cart/updateCartLine', singleData);
        bus.$on('cart-refreshed', () => {
          resolve();
        });
      });

      promises.push(promise);
      await promise;
    }
    return Promise.all(promises);
  },
  clearGWP({ commit, state }, data) {
    const productsToRemove = state.cart.items.filter((p) => p.properties._gwp);
    commit('removeAltProductToList', productsToRemove);
  },
  updateBackInStock({ commit }, payload) {
    commit('setBackInStock', payload);
  },
  setCurrentProductGalleryVideo({ commit }, payload) {
    commit('setCurrentProductGalleryVideo', payload);
  },

  setGeoSelected() {
    const expire = new Date(Date.now() + 300 * 864e5).toUTCString();
    document.cookie = `geo_selected=1; expires=${expire}; path=/`;
  },
};
